import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Wordmark from "../components/Wordmark";
import Footer from "../components/Footer";
import HoverChat from "../components/HoverChat";
import Action from "../components/Action";

/**
 * Home / OS Dashboard.
 *
 * Order:
 *   1. The Vision (anchor)
 *   2. Business (C&I)
 *   3. Investors / JV partners
 *   4. Discoms
 *   5. Consumers
 *   6. Partner with us
 *   7. Media and Curious Individuals
 *   8. Team
 *   9. Portfolio (wide row-6 capstone)
 *
 * Team was re-instated on the dashboard per the client's post-launch
 * reversal of the July 8th removal. Portfolio was promoted to a full-width
 * banner in row 6 so the 9-tile composition stays balanced.
 */

// Portfolio-tile stat rows (July 8 §A.7, refreshed with the real dataset).
const PORTFOLIO_STATS = [
  "50+ projects executed",
  "~500 MWp solar designed",
  "12 MWh+ BESS capacity",
  "31 countries served",
];

const container = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.055, delayChildren: 0.1 } },
};
const item = {
  hidden: { opacity: 0, y: 18 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
};

export default function Home() {
  return (
    <div style={{ minHeight: "100vh", position: "relative", overflow: "hidden" }}>
      {/* Ambient sunrise glow — one, quiet, per spec */}
      <div aria-hidden className="gg-sunrise gg-sun-pulse" data-testid="ambient-sunrise" />

      <header
        style={{
          padding: "26px clamp(20px, 5vw, 60px) 0",
          position: "relative",
          zIndex: 2,
        }}
      >
        <Wordmark />
      </header>

      {/* Hero — two-column on desktop, stacked on mobile. */}
      <section
        style={{
          padding: "clamp(56px, 8vw, 110px) clamp(20px, 5vw, 60px) clamp(28px, 4vw, 44px)",
          maxWidth: 1240,
          margin: "0 auto",
          position: "relative",
          zIndex: 2,
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="gg-home-hero-row"
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1.15fr) minmax(0, 1fr)",
            gap: "clamp(28px, 4vw, 60px)",
            alignItems: "center",
          }}
        >
          <div>
            <div className="gg-eyebrow" data-testid="hero-eyebrow">
              Green Gold Energy · Tamil Nadu
            </div>
            <h1
              className="gg-hero-title"
              style={{ marginTop: 12, marginBottom: 18 }}
              data-testid="hero-title"
            >
              The <span className="gg-gradient-text" style={{ whiteSpace: "nowrap" }}>Green Gold Grid</span>.
            </h1>
            <div
              style={{
                maxWidth: 640,
                color: "var(--text-dim)",
                fontSize: "clamp(15.5px, 1.6vw, 18px)",
                lineHeight: 1.6,
              }}
              data-testid="hero-subtitle"
            >
              We offer{" "}
              <HoverChat term="lfp">Solar + Indian Battery</HoverChat> (the
              Green); through opt-in{" "}
              <HoverChat term="aavin">community ownership</HoverChat> (the
              Gold); fully managed by{" "}
              <HoverChat term="griddata">griddata</HoverChat> app (the Grid).
            </div>
          </div>

          {/* Hero right — placeholder AI-generated illustration. */}
          {/* placeholder — swap when client provides real asset. */}
          <div
            data-testid="hero-right-image"
            style={{
              position: "relative",
              borderRadius: 20,
              overflow: "hidden",
              border: "1px solid rgba(244,169,31,0.28)",
              boxShadow:
                "0 0 0 1px rgba(244,169,31,0.10), 0 30px 60px -30px rgba(244,169,31,0.35)",
              background: "rgba(10,22,40,0.4)",
            }}
          >
            <img
              src="/images/hero-right.png"
              alt="The Green Gold Grid — an integrated Indian energy ecosystem"
              style={{ width: "100%", height: "auto", display: "block" }}
            />
          </div>
        </motion.div>
      </section>

      <motion.section
        variants={container}
        initial="hidden"
        animate="visible"
        style={{
          padding: "clamp(10px, 2vw, 24px) clamp(20px, 5vw, 60px) 40px",
          maxWidth: 1240,
          margin: "0 auto",
          position: "relative",
          zIndex: 2,
        }}
        aria-label="Green Gold Grid modules"
        data-testid="os-dashboard-grid"
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(12, 1fr)",
            gridAutoRows: "minmax(140px, auto)",
            gap: "clamp(12px, 1.4vw, 20px)",
          }}
          className="gg-bento"
        >
          {/* 1 — The Vision (anchor, largest). Quote fills the tile, centered
              both axes, wrapped in curly quotes. Small kicker at top, small
              link at bottom. */}
          <TileCell testId="tile-vision" col="1 / span 7" row="1 / span 3" anchor>
            <Link
              to="/vision"
              className="gg-tile gg-tile-anchor"
              data-testid="tile-vision-link"
              style={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                alignItems: "stretch",
                gap: 24,
              }}
            >
              <div className="gg-eyebrow" style={{ opacity: 0.85 }}>The Vision</div>

              <blockquote
                data-testid="tile-vision-quote"
                style={{
                  margin: 0,
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  fontFamily: "Fraunces, Georgia, serif",
                  fontStyle: "italic",
                  fontWeight: 400,
                  fontSize: "clamp(22px, 3.2vw, 40px)",
                  lineHeight: 1.28,
                  color: "var(--paper)",
                  letterSpacing: "-0.005em",
                  padding: "0 clamp(8px, 2vw, 32px)",
                }}
              >
                <span>
                  <span
                    aria-hidden
                    style={{
                      color: "var(--gold-soft)",
                      marginRight: "0.05em",
                      fontStyle: "normal",
                    }}
                  >
                    &#8220;
                  </span>
                  Our vision is to create a Vertically Integrated and
                  Horizontally Distributed Energy Ecosystem.
                  <span
                    aria-hidden
                    style={{
                      color: "var(--gold-soft)",
                      marginLeft: "0.05em",
                      fontStyle: "normal",
                    }}
                  >
                    &#8221;
                  </span>
                </span>
              </blockquote>

              <div
                data-testid="tile-vision-cta"
                style={{
                  color: "var(--text-mute)",
                  fontSize: 12.5,
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  textAlign: "center",
                }}
              >
                Explore the Vision →
              </div>
            </Link>
          </TileCell>

          {/* 2 — Business (C&I) */}
          <TileCell testId="tile-audit" col="8 / span 5" row="1 / span 2">
            <div className="gg-tile" data-testid="tile-audit-body" style={{ height: "100%" }}>
              <div>
                <h2 className="gg-tile-title">Business (C&amp;I)</h2>
                <p className="gg-tile-promise">
                  Add{" "}
                  <Action route="sovereign-audit" source="tile-audit">
                    Solar + BESS
                  </Action>{" "}
                  or{" "}
                  <Action route="sovereign-audit" source="tile-audit">
                    BESS only
                  </Action>{" "}
                  to your existing solar power. Take the{" "}
                  <Action route="sovereign-audit" source="tile-audit">
                    Sovereign Audit
                  </Action>{" "}
                  to see substantial ROI within a few years.
                </p>
              </div>
              <div>
                <div className="gg-tile-audience">Industry · ≥250 kWh</div>
              </div>
            </div>
          </TileCell>

          {/* 3 — Investors / JV partners */}
          <TileCell testId="tile-partner" col="8 / span 5" row="3 / span 1">
            <div className="gg-tile" data-testid="tile-partner-body" style={{ height: "100%" }}>
              <div>
                <h2 className="gg-tile-title">Investors / JV partners</h2>
                <p className="gg-tile-promise">
                  Invest in an actual asset, not an idea. We invite a joint
                  venture: You invest, we build a 720 MWh/year Micro-Gigafactory
                  to manufacture multi-chemistry batteries (LFP / Sodium-ion
                  with Hard Carbon Anode and Supercapacitors).{" "}
                  <Action route="request-data-room" source="tile-partner">
                    Request access to Plant Data Room
                  </Action>
                  .
                </p>
              </div>
              <div>
                <div className="gg-tile-audience">Funders · Strategics</div>
              </div>
            </div>
          </TileCell>

          {/* 4 — Discoms */}
          <TileCell testId="tile-discoms" col="1 / span 4" row="4 / span 2">
            <div className="gg-tile" data-testid="tile-discoms-body" style={{ height: "100%" }}>
              <div>
                <h2 className="gg-tile-title">Discoms</h2>
                <p className="gg-tile-promise">
                  Measure and manage every grid data and cut costs and loss
                  over 30%.{" "}
                  <Action
                    route="book-tour"
                    source="tile-discoms"
                    queryParams={{ visit_type: "griddata-pilot" }}
                  >
                    Apply for a pilot
                  </Action>
                  .
                </p>
              </div>
              <div>
                <div className="gg-tile-audience">DISCOMs · Institutions</div>
              </div>
            </div>
          </TileCell>

          {/* 5 — Consumers */}
          <TileCell testId="tile-consumers" col="5 / span 4" row="4 / span 1">
            <div className="gg-tile" data-testid="tile-consumers-body" style={{ height: "100%" }}>
              <div>
                <h2 className="gg-tile-title">Consumers</h2>
                <p className="gg-tile-promise">
                  If you could do rooftop Solar, that is great. If not, consider
                  owning a{" "}
                  <HoverChat term="fractional-ownership">
                    community energy asset
                  </HoverChat>
                  .{" "}
                  <Action route="reserve-block" source="tile-consumers">
                    Reserve a Sovereign Block
                  </Action>
                  .
                </p>
              </div>
              <div>
                <div className="gg-tile-audience">Citizens · Prosumers</div>
              </div>
            </div>
          </TileCell>

          {/* 6 — Partner with us */}
          <TileCell testId="tile-partners" col="9 / span 4" row="4 / span 1">
            <Link
              to="/partners"
              className="gg-tile"
              data-testid="tile-partners-link"
              style={{ height: "100%" }}
            >
              <div>
                <h2 className="gg-tile-title">Partner with us</h2>
                <p className="gg-tile-promise">
                  Register a deal, claim your rights, build together.
                </p>
              </div>
              <div>
                <div className="gg-tile-audience">EPCs · NBFCs · Auditors</div>
              </div>
            </Link>
          </TileCell>

          {/* 7 — Media and Curious Individuals */}
          <TileCell testId="tile-media" col="5 / span 4" row="5 / span 1">
            <div className="gg-tile" data-testid="tile-media-body" style={{ height: "100%" }}>
              <div>
                <h2 className="gg-tile-title">Media and Curious Individuals</h2>
                <p className="gg-tile-promise">
                  Want to see the making of a sovereign battery factory?{" "}
                  <Action
                    route="book-tour"
                    source="tile-media"
                    queryParams={{ visit_type: "media" }}
                  >
                    Request an invite
                  </Action>
                  .
                </p>
              </div>
              <div>
                <div className="gg-tile-audience">Public · Media · Talent</div>
              </div>
            </div>
          </TileCell>

          {/* 8 — Team (re-instated on the dashboard). */}
          <TileCell testId="tile-team" col="9 / span 4" row="5 / span 1">
            <Link
              to="/team"
              className="gg-tile"
              data-testid="tile-team-link"
              style={{ height: "100%" }}
            >
              <div>
                <h2 className="gg-tile-title">Team</h2>
                <p className="gg-tile-promise">
                  The people behind the Green Gold Grid.
                </p>
              </div>
              <div>
                <div className="gg-tile-audience">Founders · Engineers · IIT</div>
              </div>
            </Link>
          </TileCell>

          {/* 9 — Portfolio (wide capstone across all 12 columns). */}
          <TileCell testId="tile-portfolio" col="1 / span 12" row="6 / span 1">
            <Link
              to="/portfolio"
              className="gg-tile"
              data-testid="tile-portfolio-link"
              style={{ height: "100%" }}
            >
              <div
                className="gg-portfolio-tile-inner"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 28,
                  flexWrap: "wrap",
                  width: "100%",
                }}
              >
                <div style={{ minWidth: 180 }}>
                  <h2 className="gg-tile-title" style={{ marginBottom: 6 }}>
                    Portfolio
                  </h2>
                  <div className="gg-tile-audience">50+ projects · 31 countries</div>
                </div>
                <ul
                  data-testid="tile-portfolio-stats"
                  style={{
                    listStyle: "none",
                    padding: 0,
                    margin: 0,
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "10px 22px",
                    fontSize: 13,
                    color: "var(--text-dim)",
                    lineHeight: 1.5,
                  }}
                >
                  {PORTFOLIO_STATS.map((s) => (
                    <li key={s} style={{ display: "flex", gap: 8 }}>
                      <span style={{ color: "var(--gold-soft)" }}>·</span>
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Link>
          </TileCell>
        </div>

        <style>{`
          @media (max-width: 900px) {
            .gg-bento { grid-template-columns: 1fr !important; }
            .gg-bento .gg-bento-cell {
              grid-column: 1 / -1 !important;
              grid-row: auto !important;
            }
            .gg-home-hero-row {
              grid-template-columns: 1fr !important;
            }
            [data-testid="hero-right-image"] img {
              max-height: 320px;
              object-fit: cover;
            }
          }
        `}</style>

        <p
          style={{
            marginTop: 30,
            color: "var(--text-mute)",
            fontSize: 13,
            textAlign: "center",
          }}
          data-testid="dashboard-caption"
        >
          Two things are always in reach —{" "}
          <Action route="book-tour" source="home-caption">
            walk through the Tamil Nadu Factory
          </Action>{" "}
          or{" "}
          <Action route="request-data-room" source="home-caption">
            request the SPV data room
          </Action>
          .
        </p>
      </motion.section>

      <Footer />
    </div>
  );
}

function TileCell({ children, testId, col, row, anchor }) {
  return (
    <motion.div
      variants={item}
      style={{ gridColumn: col, gridRow: row }}
      className="gg-bento-cell"
      data-testid={`${testId}-cell`}
      data-anchor={anchor ? "true" : undefined}
    >
      {children}
    </motion.div>
  );
}
